import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import flagService from "./flagService";
import entryService from "../entry/entryService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";
import { setEntryValid, setEntryInvalid } from "../entry/entrySlice";

const initialState = {
  flags: [],
  flag: null,
  flagsWithEntries: [],
  playerFlags: [],
  playerFlagsLoading: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

export const getFlags = createAsyncThunk(
  "flag/getFlags",
  async (_, thunkAPI) => {
    try {
      const response = await flagService.getFlags();
      // thunkAPI.dispatch(setAlertSuccess("Flags fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch flags";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

// Flags for one player by name - shown on the My Ranking page (and while
// an admin impersonates a player). Kept separate from `flags` so loading
// it never disturbs the admin flags screen's spinner.
export const getFlagsByPlayer = createAsyncThunk(
  "flag/getFlagsByPlayer",
  async (playerName, thunkAPI) => {
    try {
      const response = await flagService.getFlagsByPlayer(playerName);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch player flags";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getFlagsWithEntries = createAsyncThunk(
  "flag/getFlagsWithEntries",
  async (_, thunkAPI) => {
    try {
      const response = await flagService.getFlags();
      const flags = response.data.flags || [];

      // Fetch entries with lookup details from the server
      const allEntries = await entryService.getEntriesWithDetails();
      const entries = allEntries.data.entries || allEntries.entries || [];

      // Normalize date format for comparison (YYYY-MM-DD)
      const normalizeDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      // Player id of an entry. Imports may store `player` as an id, an
      // embedded object, or a JSON string; `player_details` is the joined
      // row when present.
      const playerIdOf = (entry) => {
        if (entry?.player_details?.id != null) {
          return String(entry.player_details.id);
        }
        if (entry?.player && typeof entry.player === "object") {
          return entry.player.id != null ? String(entry.player.id) : null;
        }
        if (typeof entry?.player === "string" && entry.player.startsWith("{")) {
          try {
            const parsed = JSON.parse(entry.player);
            return parsed?.id != null ? String(parsed.id) : null;
          } catch {
            return null;
          }
        }
        if (
          entry?.player != null &&
          String(entry.player).trim() !== "" &&
          Number.isFinite(Number(entry.player))
        ) {
          return String(entry.player);
        }
        return null;
      };

      // Same normalisation the AI triage uses for event names.
      const normalizeEvent = (event) =>
        String(event || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      // Whole days between two dates, or null when either is unusable.
      const daysBetween = (a, b) => {
        if (!a || !b) return null;
        const first = new Date(a);
        const second = new Date(b);
        if (Number.isNaN(first.getTime()) || Number.isNaN(second.getTime())) {
          return null;
        }
        return Math.abs(Math.round((first - second) / 86400000));
      };

      // Entries already carry one flag id; the duplicate candidates also need
      // "same player" grouping.
      const entriesByPlayer = new Map();

      // Create a map of entries for easier lookup by ID and by event_name/date
      const entriesById = new Map();
      const entriesByEventDate = new Map();

      entries.forEach((entry) => {
        // Map by entry ID for direct lookup
        entriesById.set(entry.id, entry);

        // Also map by event/date for fallback matching
        const normalizedDate = normalizeDate(entry.event_date);
        const key = `${(entry.event || "").toLowerCase()}-${normalizedDate}`;
        if (!entriesByEventDate.has(key)) {
          entriesByEventDate.set(key, []);
        }
        entriesByEventDate.get(key).push(entry);

        // Group by player for duplicate candidates
        const playerId = playerIdOf(entry);
        if (playerId) {
          if (!entriesByPlayer.has(playerId)) {
            entriesByPlayer.set(playerId, []);
          }
          entriesByPlayer.get(playerId).push(entry);
        }
      });

      // Add matched entries to flags
      const flagsWithEntries = flags.map((flag) => {
        let matchedEntries = [];

        // First priority: If flag has entry_id, use it directly
        if (flag.entry_id) {
          const entry = entriesById.get(flag.entry_id);
          if (entry) {
            matchedEntries = [entry];
          }
        } else {
          // Fallback: Match by event_name and date, then filter by player_name
          const normalizedDate = normalizeDate(flag.event_date);
          const key = `${(flag.event_name || "").toLowerCase()}-${normalizedDate}`;
          const entriesByEventDateList = entriesByEventDate.get(key) || [];

          // Filter by player_name if available (case-insensitive)
          const playerFilteredEntries = entriesByEventDateList.filter(
            (entry) => {
              if (!entry.player_name && !entry.player) {
                return false; // Skip entries without player info
              }
              // Try to match by player_name if available
              if (entry.player_name) {
                return (
                  entry.player_name.toLowerCase() ===
                  flag.player_name.toLowerCase()
                );
              }
              // Otherwise return the entry (might be matched later)
              return true;
            },
          );

          matchedEntries =
            playerFilteredEntries.length > 0 ? playerFilteredEntries : [];
        }

        // Duplicate candidates: same player, same normalised event, dates
        // within 3 days — the rule aiTriageService uses.
        const sourceEntry = matchedEntries[0] || null;
        const matchedIds = new Set(
          matchedEntries.map((entry) => String(entry.id)),
        );
        let duplicateCandidates = [];
        const sourcePlayerId = playerIdOf(sourceEntry);
        if (sourcePlayerId) {
          duplicateCandidates = (entriesByPlayer.get(sourcePlayerId) || [])
            .filter((entry) => {
              if (matchedIds.has(String(entry.id))) return false;
              if (
                normalizeEvent(entry.event) !== normalizeEvent(sourceEntry.event)
              ) {
                return false;
              }
              const diff = daysBetween(entry.event_date, sourceEntry.event_date);
              return diff !== null && diff <= 3;
            })
            .sort((a, b) => {
              const diffA =
                daysBetween(a.event_date, sourceEntry.event_date) ?? 999;
              const diffB =
                daysBetween(b.event_date, sourceEntry.event_date) ?? 999;
              return diffA - diffB || Number(a.id) - Number(b.id);
            });
        }

        // The entry the AI triage named as this flag's twin, when present.
        let aiTwinEntry = null;
        const twinMatch = /Duplicate of entry (\d+)/.exec(flag.extra_info || "");
        if (twinMatch) {
          const twinId = Number(twinMatch[1]);
          aiTwinEntry =
            entriesById.get(twinId) ||
            entriesById.get(twinMatch[1]) ||
            null;
        }

        return {
          ...flag,
          entries: matchedEntries,
          duplicate_candidates: duplicateCandidates,
          ai_twin_entry: aiTwinEntry,
        };
      });

      return { data: { flags: flagsWithEntries } };
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message ||
        error.message ||
        "Failed to fetch flags with entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getFlagById = createAsyncThunk(
  "flag/getFlagById",
  async (flagId, thunkAPI) => {
    try {
      const response = await flagService.getFlagById(flagId);
      // thunkAPI.dispatch(setAlertSuccess("Flag fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const createFlag = createAsyncThunk(
  "flag/createFlag",
  async (flagData, thunkAPI) => {
    try {
      const response = await flagService.createFlag(flagData);
      thunkAPI.dispatch(setAlertSuccess("Flag created successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to create flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const updateFlag = createAsyncThunk(
  "flag/updateFlag",
  async (flagData, thunkAPI) => {
    try {
      const response = await flagService.updateFlag(flagData);
      thunkAPI.dispatch(setAlertSuccess("Flag updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to update flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const deleteFlag = createAsyncThunk(
  "flag/deleteFlag",
  async (flagId, thunkAPI) => {
    try {
      const response = await flagService.deleteFlag(flagId);
      thunkAPI.dispatch(setAlertSuccess("Flag deleted successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to delete flag";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

const flagSlice = createSlice({
  name: "flag",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      state.flag = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFlags.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flags = action.payload.data.flags || [];
      })
      .addCase(getFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getFlagsByPlayer.pending, (state) => {
        state.playerFlagsLoading = true;
      })
      .addCase(getFlagsByPlayer.fulfilled, (state, action) => {
        state.playerFlagsLoading = false;
        state.playerFlags = action.payload?.data?.flags || [];
      })
      .addCase(getFlagsByPlayer.rejected, (state, action) => {
        state.playerFlagsLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getFlagsWithEntries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlagsWithEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flagsWithEntries = action.payload.data.flags || [];
      })
      .addCase(getFlagsWithEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getFlagById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFlagById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flag = action.payload.data.flag || null;
      })
      .addCase(getFlagById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(createFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.flags.push(action.payload.data.flag);
      })
      .addCase(createFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const index = state.flags.findIndex(
          (flag) => flag.id === action.payload.data.flag.id,
        );
        if (index !== -1) {
          state.flags[index] = action.payload.data.flag;
        }
      })
      .addCase(updateFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deleteFlag.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFlag.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const deletedId = action.payload.data.deletedFlag?.id;
        state.flags = state.flags.filter((flag) => flag.id !== deletedId);
        // The admin page reads flagsWithEntries; drop it there too so the
        // review dialog closes without waiting for the refetch.
        if (deletedId != null) {
          state.flagsWithEntries = state.flagsWithEntries.filter(
            (flag) => flag.id !== deletedId,
          );
        }
      })
      .addCase(deleteFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(setEntryValid.fulfilled, (state, action) => {
        const updatedEntry = action.payload.data.entry;
        state.flagsWithEntries = state.flagsWithEntries.map((flag) => ({
          ...flag,
          entries: flag.entries.map((entry) =>
            entry.id === updatedEntry.id ? updatedEntry : entry,
          ),
        }));
      })
      .addCase(setEntryInvalid.fulfilled, (state, action) => {
        const updatedEntry = action.payload.data.entry;
        state.flagsWithEntries = state.flagsWithEntries.map((flag) => ({
          ...flag,
          entries: flag.entries.map((entry) =>
            entry.id === updatedEntry.id ? updatedEntry : entry,
          ),
        }));
      });
  },
});

export const { reset } = flagSlice.actions;
export default flagSlice.reducer;
